import React from "react";
import "./WaveDivider.scss";

interface WaveDividerProps {
  settings: {
    wave_height: number;
    wave_color: string;
    wave_opacity: number;
    wave_style: 'wavy' | 'jagged' | 'curved' | 'straight' | 'single-curve';
    flip_vertically: boolean;
    flip_horizontally: boolean;
    hide_on_mobile: boolean;
    z_index: number;
    position: 'top' | 'bottom' | 'both';
    // New render settings
    render_type?: 'all' | 'mobile-only' | 'desktop-only';
    custom_id?: string;
    custom_class?: string;
    show_section_borders?: boolean;
    background_color?: string;
  }
}

export const WaveDivider = ({ settings }: WaveDividerProps) => {
  const {
    wave_height = 50,
    wave_color = "#ffffff",
    wave_opacity = 1,
    wave_style = 'wavy',
    flip_vertically = false,
    flip_horizontally = false,
    hide_on_mobile = false,
    z_index = 1,
    position = 'bottom',
    // Destructure new settings with defaults
    render_type = 'all',
    custom_id,
    custom_class,
    show_section_borders = false,
    background_color
  } = settings;

  // Generate the appropriate SVG path based on wave_style
  const getWavePath = (style: string) => {
    switch (style) {
      case 'wavy':
        return "M0,40 C150,0 250,80 400,40 C550,0 650,80 800,40 L800,100 L0,100 Z";
      case 'jagged':
        return "M0,50 L100,0 L200,50 L300,0 L400,50 L500,0 L600,50 L700,0 L800,50 L800,100 L0,100 Z";
      case 'curved':
        return "M0,40 Q200,80 400,40 Q600,0 800,40 L800,100 L0,100 Z";
      case 'single-curve':
        return "M0,100 L800,100 L800,0 C600,60 200,60 0,0 Z";
      case 'straight':
      default:
        return "M0,40 L800,40 L800,100 L0,100 Z";
    }
  };

  // Apply transformations based on flip settings
  const getTransforms = () => {
    let transforms = [];
    if (flip_horizontally) transforms.push("scaleX(-1)");
    if (flip_vertically) transforms.push("scaleY(-1)");
    return transforms.join(" ");
  };

  const renderWave = (pos: string) => {
    const isVisible = position === 'both' || position === pos;
    if (!isVisible) return null;

    const transformClass = pos === 'top' ? 'wave-divider-top' : '';
    
    return (
      <div
        className={`wave-divider ${transformClass} ${hide_on_mobile ? 'hidden-mobile' : ''}`}
        style={{ 
          height: `${wave_height}px`,
          zIndex: z_index,
          transform: getTransforms(),
          position: 'absolute',
          [pos]: 0,
          left: 0,
          right: 0,
          overflow: 'hidden'
        }}
      >
        <svg 
          preserveAspectRatio="none" 
          viewBox="0 0 800 100" 
          style={{ 
            width: '100%', 
            height: '100%', 
            display: 'block'
          }}
        >
          <path 
            d={getWavePath(wave_style)}
            fill={wave_color} 
            fillOpacity={wave_opacity}
          />
        </svg>
      </div>
    );
  };

  // Apply CSS classes based on render settings
  const getContainerClasses = () => {
    let classes = ["wave-divider-container"];
    
    if (custom_class) {
      classes.push(custom_class);
    }
    
    if (show_section_borders) {
      classes.push("section-border");
    }
    
    return classes.join(" ");
  };

  return (
    <div 
      id={custom_id} 
      className={getContainerClasses()} 
      style={{ 
        height:`${wave_height}px`, 
        position: 'relative',
        backgroundColor: background_color || 'transparent'
      }}
    >
      {renderWave('top')}
      {renderWave('bottom')}
    </div>
  );
};